import apiClient from './client';
import { FilterParams, PaginationParams } from './paginationService';
import { VocabularyItem } from '../components/vocabulary/VocabularyItem';

// Reword .backup files can be tens of MB and the backend has to parse the whole SQLite file
// server-side before responding — the client's default 10s timeout isn't enough headroom.
// Keep in sync with mobile's REWORD_UPLOAD_TIMEOUT_MS in ImportCollectionScreen.tsx.
const REWORD_UPLOAD_TIMEOUT_MS = 120_000;
// The public view of a shared word (GET /api/vocabulary/share/:token). No image_url/
// pronunciation_url — the backend's public DTO omits them since they're proxy paths served by an
// authenticated-only route (dead links for anonymous visitors of a shared word).
export interface PublicSharedWord {
    word: string;
    translation: string[] | null; // null for a word saved without a translation
    language: string;
    explanation?: string;
    examples?: string[];
    synonyms?: string[];
}

export interface CreateVocabularyRequest {
    word: string;
    translation: string[];
    language: 'en' | 'de' | 'es' | 'fr' | 'pl' | 'ru';
    explanation?: string;
    synonyms?: string[];
    examples?: string[];
    notes?: string;
    tags?: number[];
    collectionId?: number;
    image_url?: string;
    pronunciation_url?: string;
    is_favorite?: boolean;
}
export interface UpdateVocabularyRequest {
    word: string;
    translation: string[];
    language: 'en' | 'de' | 'es' | 'fr' | 'pl' | 'ru';
    explanation?: string;
    synonyms?: string[];
    examples?: string[];
    notes?: string;
    tags?: number[];
    collectionId?: number;
    image_url?: string;
    pronunciation_url?: string;
    is_favorite?: boolean;
}

export interface ShareTokenResponse {
    share_token: string;
    share_url: string;
}

export type VocabularyImportSource = 'csv' | 'anki' | 'quizlet' | 'reword';

export type ImportFieldTarget =
    'word' | 'translation' | 'explanation' | 'notes' | 'synonyms' | 'examples' | 'image_url' | 'skip';

export interface ImportFieldMapping {
    [columnIndex: number]: ImportFieldTarget;
}

// One non-custom category from a Reword backup — see RewordCategory in reword_parser.go.
export interface RewordCategory {
    id: string;
    name_rus: string;
    word_count: number;
}

export interface ImportPreviewResult {
    headers: string[];
    rows: string[][];
    total_rows: number;
    // Only present for Reword .backup files — see RewordFileMeta in reword_parser.go. Lets the
    // user split the backup into one collection per category instead of a single target.
    categories?: RewordCategory[];
    available_languages?: string[];
}

export interface VocabularyImportOptions {
    source: VocabularyImportSource;
    language: 'en' | 'de' | 'es' | 'fr' | 'pl' | 'ru';
    collectionId?: number;
    fieldMapping?: ImportFieldMapping;
}

export interface RewordCategoryImportOptions {
    language: 'en' | 'de' | 'es' | 'fr' | 'pl' | 'ru';
    translationLanguage: string;
    selectedCategoryIds: string[];
    collectionType?: string;
    includePictures?: boolean;
    totalWords?: number;
}

export interface VocabularyImportResult {
    created: number;
    duplicates: number;
    failed: number;
    errors: string[];
    total: number;
}

export interface ImportJobResponse {
    job_id: string;
    total: number;
}

export interface ImageSearchResult {
    id: string;
    thumb_url: string;
    photographer: string;
    // "ai" for the synthetic already-cached-AI-image entry the backend prepends when one exists
    // for this word/language; omitted (undefined) for normal stock-photo results.
    source?: string;
}

export interface ImportJobStatusResult {
    id: string;
    status: 'processing' | 'done' | 'failed';
    total: number;
    created: number;
    duplicates: number;
    failed: number;
    errors: string[];
}



export const vocabularyService = {
    // Get vocabulary items with pagination and filters
    getVocabulary: async (pagination?: PaginationParams, filters?: FilterParams) => {
        let url = '/api/v1/vocabulary';
        const params: Record<string, string | number | undefined> = {};

        // Add pagination parameters if provided
        if (pagination) {
            params.page = pagination.page;
            params.pageSize = pagination.pageSize;
        }

        // Add filter parameters if provided
        if (filters) {
            if (filters.search) params.search = filters.search;
            if (filters.language) params.language = filters.language;
            if (filters.collectionId) params.collectionId = filters.collectionId;
            if (filters.tags && filters.tags.length > 0) {
                params.tags = filters.tags.join(',');
            }
            if (filters.isFavorite) params.is_favorite = 'true';
        }

        const response = await apiClient.get(url, { params });
        return response.data;
    },

    // Add new vocabulary item
    addVocabularyItem: async (item: CreateVocabularyRequest) => {
        try {
            const response = await apiClient.post('/api/v1/vocabulary', item);
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 401) {
                // Let the interceptor handle token refresh
                throw error;
            }
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw new Error('Failed to add vocabulary item');
        }
    },

    // Get a single vocabulary item by ID
    getVocabularyItem: async (id: string) => {
        try {
            const response = await apiClient.get(`/api/v1/vocabulary/${id}`);
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw new Error('Failed to get vocabulary item');
        }
    },

    // Update vocabulary item
    updateVocabularyItem: async (id: string, item: UpdateVocabularyRequest) => {
        try {
            // Looking at the backend handler, it only updates fields that are provided
            // and non-empty, so we need to make sure all fields are included
            const updateData: UpdateVocabularyRequest = {
                word: item.word,
                translation: item.translation || [],
                language: item.language,
                explanation: item.explanation || '',
                examples: item.examples || [],
                synonyms: item.synonyms || [],
                notes: item.notes || '',
                tags: item.tags || []
            };

            // The backend handler checks if CollectionId is nil, so we need to
            // only include it if it's defined
            if (item.collectionId !== undefined && item.collectionId !== null) {
                updateData['collectionId'] = item.collectionId;
            }
            if (item.image_url !== undefined) {
                updateData['image_url'] = item.image_url;
            }
            if (item.pronunciation_url !== undefined) {
                updateData['pronunciation_url'] = item.pronunciation_url;
            }
            if (item.is_favorite !== undefined) {
                updateData['is_favorite'] = item.is_favorite;
            }

            console.log('Updating vocabulary item:', id, updateData);
            const response = await apiClient.put(`/api/v1/vocabulary/${id}`, updateData);
            return response.data;
        } catch (error: any) {
            console.error('Update error:', error.response?.data);
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw new Error('Failed to update vocabulary item');
        }
    },

    // Update only the notes field for a vocabulary item.
    updateNotes: async (id: string, notes: string): Promise<void> => {
        await apiClient.put(`/api/v1/vocabulary/${id}`, { notes });
    },

    // Link an image URL to a vocabulary item (targeted single-column update).
    updateImageUrl: async (id: string, imageUrl: string): Promise<void> => {
        await apiClient.put(`/api/v1/vocabulary/${id}/image`, { image_url: imageUrl });
    },

    // Delete the stored image file and clear image_url on the vocabulary item.
    deleteImage: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/v1/vocabulary/${id}/image`);
    },

    // Link a pronunciation URL to a vocabulary item (targeted single-column update).
    updatePronunciationUrl: async (id: string, url: string): Promise<void> => {
        await apiClient.put(`/api/v1/vocabulary/${id}/pronunciation`, { pronunciation_url: url });
    },

    // Delete the stored pronunciation audio and clear the field on the vocabulary item.
    deletePronunciation: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/v1/vocabulary/${id}/pronunciation`);
    },

    // Delete vocabulary item
    deleteVocabularyItem: async (id: string) => {
        const response = await apiClient.delete(`/api/v1/vocabulary/${id}`);
        return response.data;
    },

    // Public, no auth — the shared word behind /w/:token (PublicWordPage.tsx).
    getPublicByShareToken: async (token: string): Promise<PublicSharedWord> => {
        const response = await apiClient.getPublic(`/api/vocabulary/share/${encodeURIComponent(token)}`);
        return response.data as PublicSharedWord;
    },

    // Mint (or fetch the existing) public share link for this word — the public page
    // it points to is already live at /w/:token (see PublicWordPage.tsx).
    getShareLink: async (id: string): Promise<ShareTokenResponse> => {
        const response = await apiClient.post(`/api/v1/vocabulary/${id}/share`);
        return response.data as ShareTokenResponse;
    },

    // Fetch vocabulary sorted by SR weakness (learning first, lowest ease-factor first).
    getVocabularyWithSR: async (filters?: FilterParams): Promise<{ items: VocabularyItem[] }> => {
        const params: Record<string, string | number | undefined> = {};
        if (filters) {
            if (filters.language) params.language = filters.language;
            if (filters.collectionId) params.collectionId = filters.collectionId;
            if (filters.isFavorite) params.is_favorite = 'true';
        }
        const response = await apiClient.get('/api/v1/vocabulary/with-sr', { params });
        return response.data;
    },

    // Every vocabulary item the user has in one language, unpaginated — used by the lesson
    // feature to match target-vocabulary words against the learner's own collection in a single
    // call, rather than one search request per word (see VocabularyHandler.ListByLanguage).
    getVocabularyByLanguage: async (language: string): Promise<VocabularyItem[]> => {
        const response = await apiClient.get(`/api/v1/vocabulary/language/${language}`);
        return response.data ?? [];
    },

    // Toggle the favorite status of a vocabulary item.
    toggleFavorite: async (id: string, isFavorite: boolean): Promise<void> => {
        await apiClient.put(`/api/v1/vocabulary/${id}`, { is_favorite: isFavorite });
    },

    importVocabulary: async (file: File, options: VocabularyImportOptions): Promise<ImportJobResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', options.source);
        formData.append('language', options.language);
        if (options.collectionId) {
            formData.append('collectionId', String(options.collectionId));
        }
        if (options.fieldMapping && Object.keys(options.fieldMapping).length > 0) {
            formData.append('fieldMapping', JSON.stringify(options.fieldMapping));
        }

        const response = await apiClient.post('/api/v1/vocabulary/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data as ImportJobResponse;
    },

    // Splits a Reword .backup into one Collection per selected Reword category — unlike
    // importVocabulary (single target collection), each category becomes its own new
    // collection, named after the category. Mirrors mobile's handleImportByCategory.
    importRewordCategories: async (file: File, options: RewordCategoryImportOptions): Promise<ImportJobResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('language', options.language);
        formData.append('translation_language', options.translationLanguage);
        formData.append('selected_categories', JSON.stringify(options.selectedCategoryIds));
        formData.append('collection_type', options.collectionType || 'vocabulary');
        formData.append('include_pictures', options.includePictures === false ? '0' : '1');
        if (options.totalWords) {
            formData.append('total_words', String(options.totalWords));
        }

        const response = await apiClient.post('/api/v1/vocabulary/import/reword-categories', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeoutMs: REWORD_UPLOAD_TIMEOUT_MS,
        });
        return response.data as ImportJobResponse;
    },

    getImportStatus: async (jobId: string): Promise<ImportJobStatusResult> => {
        const response = await apiClient.get(`/api/v1/vocabulary/import/status/${jobId}`);
        return response.data as ImportJobStatusResult;
    },

    previewImport: async (file: File, source: VocabularyImportSource): Promise<ImportPreviewResult> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', source);

        const response = await apiClient.post('/api/v1/vocabulary/import/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeoutMs: source === 'reword' ? REWORD_UPLOAD_TIMEOUT_MS : undefined,
        });
        return response.data as ImportPreviewResult;
    },

    // Returns a shared, globally cached image for this item's word if one already exists
    // (cached: true, no OpenAI call), unless force is set — which declines the cached image and
    // generates a fresh one (cached: false).
    generateImage: async (id: string, options?: { force?: boolean }): Promise<{ image_url: string; cached: boolean }> => {
        const response = await apiClient.post(
            `/api/v1/vocabulary/${id}/generate-image`,
            { force: !!options?.force },
            // A cache-miss generation routinely takes ~60s server-side — well past the client's
            // default 10s timeout, which was aborting the request out from under the backend
            // before it could ever finish (see the backend's own 100s handler timeout,
            // vocabulary_image_handler.go). Keep in sync with mobile's vocabularyService.ts.
            { timeoutMs: 110_000 },
        );
        return response.data as { image_url: string; cached: boolean };
    },

    // Search for stock photos (proxied through the backend, currently backed by Pexels — the
    // provider's key never reaches the client), plus — when `language` is passed — an
    // already-cached AI-generated image for this word, if one exists (result.source === 'ai'),
    // so the user can reuse it instead of paying for a fresh generation. Returns [] if neither is
    // configured/found server-side.
    // `signal` lets a caller cancel an in-flight search it no longer cares about (e.g. superseded
    // by a newer search) instead of letting it run to completion against the backend/Pexels proxy.
    searchImages: async (query: string, signal?: AbortSignal, language?: string): Promise<ImageSearchResult[]> => {
        const response = await apiClient.get('/api/v1/vocabulary/images/search', { params: { q: query, language }, signal });
        return (response.data.results as ImageSearchResult[]) ?? [];
    },

    // Fetch a chosen search result by ID and store it as the item's image (same flow as generateImage).
    selectImage: async (id: string, resultId: string): Promise<string> => {
        const response = await apiClient.post(`/api/v1/vocabulary/${id}/select-image`, { result_id: resultId });
        return response.data.image_url as string;
    },
};